import React, { useState, useMemo, useRef, useEffect } from "react";

import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { makeStyles } from "tss-react/mui";

import { useUserContext, useUserContextSetters } from "contexts/UserContext";
import { UserMe } from "api/authTypes";
import { AutomationSpace } from "api/automationTypes";

const useStyles = makeStyles()((theme) => ({
  outer: {
    marginBottom: theme.spacing(2),
  },
  dialog: {
    margin: theme.spacing(3, 2),
    padding: 0,
    width: "70vw",
    maxWidth: "calc(600px * 0.7)",
  },
  chip: {
    boackgroundColor: theme.palette.secondary.light,
    textColor: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(0.5),
  },
}));

export const SpaceSelector = ({
  filterSpaces,
  onNoSpaceAvailable,
}: SpaceSelectorProps) => {
  const { classes } = useStyles();
  const { currentSpace, automationSpaces, userProfile } = useUserContext();
  const { setCurrentSpace } = useUserContextSetters();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentFilter, setCurrentfilter] = useState<string>("");
  const [chosenSpace, setChosenSpace] =
    useState<UserMe["automationSpaces"][0]>();
  const [minHeight, setMinHeight] = useState<number>(0);
  const dialogRef = useRef();

  useEffect(() => {
    // if the space currently selected doesn't fit the requirements of the current page
    if (
      filterSpaces &&
      currentSpace &&
      automationSpaces.length &&
      !filterSpaces(currentSpace)
    ) {
      const validSpace = automationSpaces.find(filterSpaces);
      if (validSpace) {
        setCurrentSpace(validSpace);
      } else {
        onNoSpaceAvailable && onNoSpaceAvailable();
      }
    } else if (userProfile && !automationSpaces.length) {
      onNoSpaceAvailable && onNoSpaceAvailable();
    }
  }, [
    filterSpaces,
    currentSpace,
    setCurrentSpace,
    automationSpaces,
    onNoSpaceAvailable,
    userProfile,
  ]);

  const spacesList = useMemo(() => {
    const filteredGroupedSpaces = {};

    automationSpaces.forEach((space) => {
      if (
        space.schoolName
          .toLocaleLowerCase()
          .includes(currentFilter.toLocaleLowerCase()) ||
        space.brand
          .toLocaleLowerCase()
          .includes(currentFilter.toLocaleLowerCase())
      ) {
        if (Object.keys(filteredGroupedSpaces).includes(space.brand)) {
          filteredGroupedSpaces[space.brand].push(space);
        } else {
          filteredGroupedSpaces[space.brand] = [space];
        }
      }
    });

    return filteredGroupedSpaces;
  }, [currentFilter, automationSpaces]);

  const handleOpenButtonPress = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
    setCurrentfilter("");
    setChosenSpace(undefined);
  };

  const handleSelection = () => {
    if (chosenSpace) {
      setCurrentSpace(chosenSpace);
    }
    onClose();
  };

  const handleSearch = ({ target: { value } }) => {
    // this keeps the Dialog from shrinking when searching
    if (minHeight === 0) {
      // @ts-expect-error
      setMinHeight(dialogRef.current?.clientHeight);
    }

    setCurrentfilter(value);
  };

  if (automationSpaces.length < 2) {
    return null;
  }

  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      className={classes.outer}
    >
      <Typography
        variant="subtitle1"
        color="text.primary"
        fontWeight="bold"
        textTransform="capitalize"
      >
        {`Grupo ${currentSpace?.brand}`}

        <Typography component="span">
          {` | ${currentSpace?.brand} ${currentSpace?.schoolName}`}
        </Typography>
      </Typography>

      <Button
        color="secondary"
        variant="outlined"
        onClick={handleOpenButtonPress}
      >
        <Typography variant="caption">Alterar</Typography>
      </Button>

      <Dialog open={isOpen} onClose={onClose}>
        <DialogContent
          className={classes.dialog}
          ref={dialogRef}
          sx={{ minHeight }}
        >
          <TextField
            variant="outlined"
            fullWidth
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon />,
            }}
          />
          {Object.keys(spacesList).map((key) => {
            return (
              <Grid key={key}>
                <Typography
                  variant="body2"
                  textTransform="capitalize"
                  sx={{ mt: 1 }}
                >
                  {key}
                </Typography>
                <Grid container item sx={{ mt: 1 }}>
                  {spacesList[key]?.map((space) => (
                    <Chip
                      clickable
                      disabled={filterSpaces ? !filterSpaces(space) : false}
                      key={space.id}
                      label={
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          color={
                            chosenSpace?.id === space.id ||
                            (!chosenSpace && currentSpace?.id === space.id)
                              ? "text.secondary"
                              : "text.primary"
                          }
                        >
                          {space.schoolName}
                        </Typography>
                      }
                      onClick={() => setChosenSpace(space)}
                      className={classes.chip}
                      color={
                        chosenSpace?.id === space.id ||
                        (!chosenSpace && currentSpace?.id === space.id)
                          ? "primary"
                          : undefined
                      }
                    />
                  ))}
                </Grid>
                <Divider />
              </Grid>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ pb: 3, pr: 2, pl: 2 }}>
          <Button fullWidth variant="contained" onClick={handleSelection}>
            Selecionar
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export type SpaceSelectorProps = {
  /* Should return true if a space fits the requirements and false otherwise */
  filterSpaces?: (space: AutomationSpace) => boolean;
  onNoSpaceAvailable?: () => void;
};
